import React from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { NotificationStats } from '@/mockdata/notifications'
import { useAppTheme } from '@/theme/ThemeContext'

export interface NotificationStatsCardProps {
  item: NotificationStats
  onClick?: (item: NotificationStats) => void
  isEditMode?: boolean
  isDraggable?: boolean
  onDragStart?: () => void
  onDrop?: (dragIndex: number) => void
  index?: number
}

export const NotificationStatsCard: React.FC<NotificationStatsCardProps> = ({
  item,
  onClick,
  isEditMode = false,
  isDraggable = false,
  onDragStart,
  onDrop,
  index = 0,
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null)

  const colors = {
    background: isDarkMode ? '#003089' : '#E3F5FF',
    titleText: isDarkMode ? '#FFFFFF' : '#003F61',
    countText: isDarkMode ? '#FFFFFF' : '#003F61',
    unitText: isDarkMode ? '#B0B0B0' : '#969696',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(107, 126, 157, 0.1)',
    shadow: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(107, 126, 157, 0.15)',
    hoverShadow: isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(107, 126, 157, 0.25)',
    hoverBorder: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(107, 126, 157, 0.2)',
    iconColor: isDarkMode ? '#E5E7EB' : '#6B7E9D',
  }

  const handleDragStart = (e: React.DragEvent) => {
    if (!isDraggable) return
    setDraggedIndex(index)
    e.dataTransfer.setData('text/plain', index.toString())
    onDragStart?.()
  }

  const handleDragOver = (e: React.DragEvent) => {
    if (!isDraggable) return
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    if (!isDraggable) return
    e.preventDefault()
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'))
    if (dragIndex !== index) {
      onDrop?.(dragIndex)
    }
    setDraggedIndex(null)
  }

  return (
    <Box
      onClick={() => !isEditMode && onClick?.(item)}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        backgroundColor: colors.background,
        borderRadius: '12px',
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        cursor: isDraggable ? 'grab' : onClick ? 'pointer' : 'default',
        opacity: draggedIndex === index ? 0.5 : 1,
        height: '53px',
        width: '246px',
        position: 'relative',
        margin: '0 0 10px 0',
        boxShadow: `0 2px 8px ${colors.shadow}`,
        marginLeft: '15px',
        transition: 'all 0.3s ease',
        border: `1px solid ${colors.border}`,
        '&:hover': onClick
          ? {
            transform: 'translateY(-3px)',
            boxShadow: `0 6px 20px ${colors.hoverShadow}`,
            borderColor: colors.hoverBorder,
          }
          : {},
        direction: 'rtl',
      }}
    >
      {isEditMode && (
        <IconButton
          size="small"
          sx={{
            color: colors.iconColor,
            order: 1,
            marginLeft: '4px',
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          aria-label="drag to reorder"
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      )}
      

      <Box
        sx={{
          flex: 1,
          textAlign: 'right',
          order: 1,
          marginLeft: '10px',
        }}
        
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 200,
            color: colors.titleText,
            fontSize: '14px',
            mb: -0.7,
            fontFamily: 'Rubik, sans-serif',
          }}
        >
          {item.title}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          right: '70%', 
          width: '1.8px',
          height: '80%',
          backgroundColor: colors.border,
          opacity: 0.8,
        }}
      />
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          order: 2,
          fontSize: '26px',
          fontWeight: 200,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 300,
            color: colors.countText,
            fontSize: '20px',
            lineHeight: 1,
            mb: 0.2,
            textAlign: 'center',
            marginTop: '10px',
            fontFamily: 'Rubik, sans-serif',
          }}
        >
          {item.count}
        </Typography>
        
        <Typography
          variant="caption"
          sx={{
            color: colors.unitText,
            fontSize: '14px',
            textAlign: 'center',
            fontWeight: 100,
            fontFamily: 'Rubik, sans-serif',
          }}
        >
          יח׳
        </Typography>
      </Box>
    </Box>
  )
}

export default NotificationStatsCard
